/* =============================================================================
 *  Kreux Matrix — Legal · /terms  (AGB · B2B)
 *  Standalone route · designed to open in a new tab from the Terminal footer.
 *  Bilingual EN/DE via global useLang() · zero hardcoded brand-palette hex.
 *  Brand vars only · pure Tailwind transitions · zero React state (beyond dict).
 * ===========================================================================*/

import { useLang } from "@/lib/LangContext";
import LegalLangToggle from "@/components/LegalLangToggle";

const dict = {
  en: {
    Title: "Terms of Service (B2B)",
    Subtitle: "Kreux Matrix — Last Updated: June 2026",
    Eyebrow: "B2B Custom Quote · Master Agreement",
    CloseTab: "Close Tab",
    BackToSite: "Back to Kreux Matrix",
    Sections: [
      {
        heading: "1. Scope & B2B Exclusivity",
        body: "These Terms apply to all business relationships regarding AI automations between Kreux Matrix and the client. Our services are directed exclusively at businesses and commercial entities (B2B)."
      },
      {
        heading: "2. Contract Formation",
        body: "A binding contract is only formed when the client officially accepts a custom, written proposal provided by the Provider after a consultation."
      },
      {
        heading: "3. Pricing & Payment",
        body: "Due to the bespoke nature of our AI solutions, all prices are calculated individually. Invoicing and payment processing will be governed by the specific terms outlined in the accepted proposal."
      },
      {
        heading: "4. Nature of AI Services & Technical Disclaimer",
        body: "Our AI solutions are designed to highly optimize and augment business processes. Because Large Language Models operate probabilistically, they function as advanced assistive systems rather than autonomous decision-makers. The Customer retains final operational oversight and decision-making authority over AI-generated outputs, ensuring maximum control over their business operations."
      },
      {
        heading: "5. Limitation of Liability",
        body: "Liability for slight negligence is entirely excluded unless essential contractual cardinal duties are breached. We are expressly not liable for indirect damages or operational failures resulting from third-party API dependencies (e.g., OpenAI, Anthropic). When utilizing third-party AI models (e.g., OpenAI, Anthropic), the Provider exclusively uses secure Enterprise API endpoints that strictly prohibit the use of client data for public model training, ensuring maximum data confidentiality."
      }
    ]
  },
  de: {
    Title: "Allgemeine Geschäftsbedingungen (B2B)",
    Subtitle: "Kreux Matrix — Zuletzt aktualisiert: Juni 2026",
    Eyebrow: "B2B Individualangebot · Rahmenvertrag",
    CloseTab: "Tab Schließen",
    BackToSite: "Zurück zu Kreux Matrix",
    Sections: [
      {
        heading: "1. Geltungsbereich & B2B-Exklusivität",
        body: "Diese AGB gelten für alle Geschäftsbeziehungen über KI-Automatisierungen zwischen Kreux Matrix und dem Kunden. Unser Angebot richtet sich ausschließlich an Unternehmer und Gewerbetreibende (B2B)."
      },
      {
        heading: "2. Vertragsschluss",
        body: "Ein verbindlicher Vertrag kommt erst zustande, wenn der Kunde ein individuell erstelltes, schriftliches Angebot annimmt."
      },
      {
        heading: "3. Preise & Zahlungsbedingungen",
        body: "Alle Preise werden projektspezifisch kalkuliert. Die Rechnungsstellung und Zahlungsabwicklung richten sich nach den im Angebot festgelegten Bedingungen."
      },
      {
        heading: "4. Art der KI-Dienstleistungen & Haftungsausschluss",
        body: "Unsere KI-Lösungen sind darauf ausgelegt, Geschäftsprozesse maßgeblich zu optimieren und zu unterstützen. Da Sprachmodelle probabilistisch arbeiten, fungieren sie als hochmoderne Assistenzsysteme und nicht als autonome Entscheidungsträger. Der Kunde behält stets die operative Kontrolle und die abschließende Entscheidungsbefugnis über die KI-generierten Ergebnisse, um maximale Sicherheit im Betriebsablauf zu gewährleisten."
      },
      {
        heading: "5. Haftungsbeschränkung",
        body: "Die Haftung für leichte Fahrlässigkeit ist ausgeschlossen. Eine Haftung für mittelbare Schäden oder Ausfälle, die durch Drittanbieter-APIs (z.B. OpenAI, Anthropic) entstehen, ist ausdrücklich ausgeschlossen. Bei der Nutzung von Drittanbieter-KI-Modellen (z.B. OpenAI, Anthropic) verwendet der Anbieter ausschließlich sichere Enterprise-API-Schnittstellen, die die Nutzung von Kundendaten für das Training öffentlicher Modelle strengstens untersagen, um maximale Vertraulichkeit zu gewährleisten."
      }
    ]
  }
};

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ff176637-490c-4d8d-8821-0039665e7b20/artifacts/1ppcjvci_Web%20logo.png";

const HexMatrix = () => (
  <svg
    aria-hidden="true"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute inset-0"
  >
    <defs>
      <pattern id="kx-hex-tos" x="0" y="0" width="56" height="98" patternUnits="userSpaceOnUse">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M0 48 L0 80 L28 96 L56 80 L56 48"
          fill="none"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#kx-hex-tos)" />
  </svg>
);

const TermsOfService = () => {
  const { lang } = useLang();
  const t = dict[lang] || dict.en;

  return (
    <main
      data-testid="terms-of-service-page"
      className="relative w-full min-h-screen font-sans overflow-x-hidden"
      style={{
        "--il-white": "#FFFFFF",
        "--il-cyan":  "#00E5FF",
        "--il-steel": "#1B3245",
        "--il-navy":  "#112332",
        "--il-amber": "#FFB300",
        background:
          "linear-gradient(180deg, #050B12 0%, #0A1520 40%, #03070C 100%)",
        color: "#FFFFFF",
      }}
    >
      {/* Hex matrix bg */}
      <div className="absolute inset-0 z-0 opacity-25 pointer-events-none animate-pulse" style={{ animationDuration: "8s" }}>
        <HexMatrix />
      </div>

      {/* In-page EN/DE toggle for direct-link visitors */}
      <LegalLangToggle />

      {/* Ambient cyan glow blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[var(--il-cyan)]/5 blur-[120px] z-0"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[8%] left-[-120px] w-[420px] h-[420px] rounded-full bg-[var(--il-cyan)]/5 blur-[120px] z-0"
      />

      {/* Top nav */}
      <nav
        data-testid="tos-nav"
        className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 py-8 flex items-center justify-between"
      >
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 no-underline group"
          data-testid="tos-brand"
        >
          <img
            src={LOGO_URL}
            alt="Kreux Matrix"
            className="w-8 h-8 object-contain select-none"
            draggable={false}
          />
          <span className="text-xl md:text-2xl font-light tracking-tighter text-white uppercase">
            Kreux Matrix
          </span>
        </a>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tos-back-link"
          className="text-xs font-mono text-white/50 hover:text-[var(--il-cyan)] tracking-[0.2em] uppercase transition-colors duration-300 no-underline"
        >
          ← {t.BackToSite}
        </a>
      </nav>

      {/* Content card */}
      <section className="relative z-10 w-full max-w-4xl mx-auto px-6 lg:px-12 pb-24 pt-8">
        <div
          data-testid="tos-card"
          className="relative rounded-3xl bg-white/5 backdrop-blur-3xl border border-[var(--il-cyan)]/20 shadow-[0_0_80px_-20px_var(--il-cyan)] hover:shadow-[0_0_120px_-20px_var(--il-cyan)] hover:border-[var(--il-cyan)]/40 transition-all duration-700 ease-out p-10 md:p-16 overflow-hidden"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(0,229,255,0.6), transparent)",
            }}
          />

          <span
            className="block font-mono text-[10px] tracking-[0.32em] uppercase text-[var(--il-cyan)]/80 mb-4"
            data-testid="tos-eyebrow"
          >
            {t.Eyebrow}
          </span>

          <h1
            data-testid="tos-title"
            className="text-4xl md:text-6xl font-light tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white/80 to-white/20 drop-shadow-[0_0_30px_rgba(255,255,255,0.1)] mb-4 m-0 leading-tight pb-2"
          >
            {t.Title}
          </h1>

          <p
            className="text-sm font-mono text-white/40 tracking-widest uppercase m-0 mb-10"
            data-testid="tos-subtitle"
          >
            {t.Subtitle}
          </p>

          <span
            aria-hidden="true"
            className="block w-16 h-px mb-12"
            style={{
              background: "linear-gradient(90deg, var(--il-cyan), transparent)",
              boxShadow: "0 0 12px var(--il-cyan)",
            }}
          />

          <div className="flex flex-col gap-12" data-testid="tos-sections">
            {t.Sections.map((s, i) => (
              <article key={i} data-testid={`tos-section-${i}`}>
                <h2 className="text-sm md:text-base font-mono tracking-[0.22em] uppercase text-[var(--il-cyan)] mb-5 leading-snug">
                  {s.heading}
                </h2>
                <div className="flex flex-col gap-4">
                  {s.body.split("\n\n").map((para, j) => (
                    <p
                      key={j}
                      className="text-base md:text-lg text-white/70 font-light leading-relaxed m-0 whitespace-pre-line break-words"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-16 pt-10 border-t border-white/10 flex items-center justify-center">
            <button
              type="button"
              data-testid="tos-close-tab"
              onClick={() => window.close()}
              className="relative group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md text-sm font-mono uppercase tracking-[0.22em] text-white/70 hover:text-[var(--il-cyan)] hover:border-[var(--il-cyan)]/60 hover:bg-[var(--il-cyan)]/[0.06] hover:shadow-[0_0_40px_-10px_var(--il-cyan)] transition-all duration-500 ease-out cursor-pointer"
            >
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rotate-45 bg-[var(--il-cyan)] shadow-[0_0_10px_var(--il-cyan)] group-hover:scale-125 transition-transform duration-500"
              />
              <span>{t.CloseTab}</span>
            </button>
          </div>
        </div>

        <div className="mt-10 text-center">
          <span className="text-[10px] font-mono text-white/30 tracking-widest uppercase">
            © 2026 Kreux Matrix · All rights reserved
          </span>
        </div>
      </section>
    </main>
  );
};

export default TermsOfService;
